import React, { useEffect, useRef, useState } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { ChevronDownIcon } from '../../icons';
import { fontFamilies } from '../../tokens';
import { Colors } from '../../../../themes/colors';
import { TypeIcon } from './TypeIcon';
import { typeColors, typeLabels, TypeVariant } from './types';

export interface TypeDropdownProps {
  /** Currently selected type */
  value: TypeVariant;
  /** Callback when type is changed */
  onChange?: (type: TypeVariant) => void;
  /** Whether to show label (true) or just icon (false) */
  showLabel?: boolean;
  /** Shape variant: 'default' (rounded 4px) or 'pill' (rounded 99px) */
  shape?: 'default' | 'pill';
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Test ID for testing */
  testID?: string;
  /** Available types to show in dropdown (defaults to all BASE_TYPES) */
  availableTypes?: TypeVariant[];
}

// Base types for the dropdown (excluding aliases)
const BASE_TYPES: TypeVariant[] = ['attention', 'blocker', 'done', 'routines', 'note', 'campaign'];

/**
 * TypeDropdown - Dropdown for selecting type status
 *
 * From Figma Crop Circle System
 * - Types Dropdown (icon only): Node 583-1627
 * - Type Dropdown Options (with label): Node 1312-1014
 *
 * Specs from Figma:
 * - Background: Type color
 * - Corner radius: 4px
 * - Icon-only: Padding 12px horizontal, 4px vertical, gap 6px
 * - With label: Padding 8px horizontal, 4px vertical, gap 4px
 * - Font: Inter 400 12px, white
 */
export const TypeDropdown: React.FC<TypeDropdownProps> = ({
  value,
  onChange,
  showLabel = false,
  shape = 'default',
  disabled = false,
  testID,
  availableTypes = BASE_TYPES,
}) => {
  const isPill = shape === 'pill';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownAnim = useRef(new Animated.Value(0)).current;
  const chevronAnim = useRef(new Animated.Value(0)).current;

  const backgroundColor = typeColors[value];
  const label = typeLabels[value];

  // Filter out the currently selected type from dropdown options
  const dropdownTypes = availableTypes.filter((type) => type !== value);

  useEffect(() => {
    Animated.timing(dropdownAnim, {
      toValue: isOpen ? 1 : 0,
      duration: 200,
      useNativeDriver: true,
    }).start();
    // Spring rotation matches the canonical Crop Circle pattern (see AddButton).
    Animated.spring(chevronAnim, {
      toValue: isOpen ? 1 : 0,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();
  }, [isOpen, dropdownAnim, chevronAnim]);

  const handleSelect = (type: TypeVariant) => {
    onChange?.(type);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // When open, always show label; when closed, respect showLabel prop
  const shouldShowLabel = isOpen && showLabel;

  const chevronRotation = chevronAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const dropdownOpacity = dropdownAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const dropdownTranslateY = dropdownAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-8, 0],
  });

  // Determine trigger style based on showLabel prop (not isOpen state)
  // This keeps height consistent between open/closed states
  const triggerStyle = showLabel ? styles.triggerWithLabel : styles.triggerIconOnly;

  return (
    <View style={[styles.container, { position: 'relative' }]}>
      <Pressable
        style={[
          styles.trigger,
          triggerStyle,
          { backgroundColor, borderRadius: isPill ? 99 : 4 },
          isPill && styles.triggerPill,
          disabled && styles.disabled,
        ]}
        onPress={handleToggle}
        disabled={disabled}
        testID={testID}
      >
        <TypeIcon variant={value} size={16} color={Colors.role.textOnBrand} />
        {shouldShowLabel && <Text style={styles.label}>{label}</Text>}
        <Animated.View style={{ transform: [{ rotate: chevronRotation }] }}>
          <ChevronDownIcon size={16} color={Colors.role.textOnBrand} />
        </Animated.View>
      </Pressable>

      {isOpen && (
        <Animated.View
          style={[
            styles.dropdownMenu,
            {
              opacity: dropdownOpacity,
              transform: [{ translateY: dropdownTranslateY }],
            },
          ]}
        >
          {dropdownTypes.map((type) => (
            <Pressable
              key={type}
              style={[styles.menuItem, { backgroundColor: typeColors[type], borderRadius: isPill ? 99 : 4 }]}
              onPress={() => handleSelect(type)}
            >
              <TypeIcon variant={type} size={16} color={Colors.role.textOnBrand} />
              <Text style={styles.menuItemLabel}>{typeLabels[type]}</Text>
            </Pressable>
          ))}
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'flex-start',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
  },
  triggerIconOnly: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 6,
  },
  triggerWithLabel: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 4,
  },
  triggerPill: {
    minHeight: 32,
  },
  label: {
    fontSize: 12,
    fontFamily: fontFamilies.primary,
    fontWeight: '400',
    color: Colors.role.textOnBrand,
  },
  disabled: {
    opacity: 0.5,
  },
  dropdownMenu: {
    marginTop: 45,
    backgroundColor: Colors.role.surface,
    borderRadius: 4,
    padding: 4,
    gap: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    position: 'absolute',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 4,
    gap: 8,
    minWidth: 110,
  },
  menuItemLabel: {
    flex: 1,
    fontSize: 12,
    fontFamily: fontFamilies.primary,
    fontWeight: '400',
    color: Colors.role.textOnBrand,
    textAlignVertical: 'center',
  },
});

TypeDropdown.displayName = 'TypeDropdown';
